import { SparklesIcon } from "@heroicons/react/outline";
import { SparklesIcon as SparklesIcon2 } from "@heroicons/react/solid";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";

import LoadingSpinner from "../LoadingSpinner";
import { BasicTooltip } from "../ui/Tooltip";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

import { useAIDatasetAssistant } from "@/queries/playground/useAIDatasetAssistant";
import { IS_AI_FEATURES_ENABLED } from "@/constants";
import SchemaEditor from "@/components/SchemaEditor";
import { INTERACTIVE_EDITOR_DEFAULT_SCHEMA } from "@/components/SchemaEditor/constants";

const loadingPhrases = [
  "Generating dataset variables...",
  "Crafting input schema...",
  "Analyzing data patterns...",
  "Building variable structure...",
  "Processing dataset logic...",
  "Organizing data fields...",
  "Structuring variables...",
  "Mapping data relationships...",
];

interface AIDatasetAssistantProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesGenerated: (variables: Record<string, string>) => void;
  promptTemplate?: string;
}

const getSchemaButtonText = (showSchemaEditor: boolean, rowSchema: unknown): string => {
  if (showSchemaEditor) {
    return "Hide Editor";
  }

  if (rowSchema) {
    return "Edit Schema";
  }

  return "Define Schema";
};

export const AIDatasetAssistant = ({
  isOpen,
  onOpenChange,
  onVariablesGenerated,
  promptTemplate = "",
}: AIDatasetAssistantProps) => {
  const [description, setDescription] = useState("");
  const [rowSchema, setRowSchema] = useState<object | null>(null);
  const [showSchemaEditor, setShowSchemaEditor] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);

  const { mutate: generateDataset, isLoading } = useAIDatasetAssistant();

  const handleGenerateDataset = useCallback(() => {
    if (!description.trim()) {
      setIsShaking(true);
      return;
    }

    generateDataset(
      {
        description,
        prompt_template: promptTemplate,
        row_schema: rowSchema || null,
      },
      {
        onSuccess: data => {
          onVariablesGenerated(data.variables);
          // Reset form state
          setDescription("");
          setRowSchema(null);
          setShowSchemaEditor(false);
          onOpenChange(false);
        }, // eslint-disable-next-line @typescript-eslint/no-empty-function
        onError: () => {},
      },
    );
  }, [generateDataset, description, promptTemplate, rowSchema, onVariablesGenerated, onOpenChange]);

  useEffect(() => {
    if (!isOpen && !isLoading) {
      setDescription("");
      setRowSchema(null);
      setShowSchemaEditor(false);
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentPhraseIndex(prev => (prev + 1) % loadingPhrases.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [isLoading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!description.trim()) {
          setIsShaking(true);
          return;
        }
        handleGenerateDataset();
      }
    },
    [handleGenerateDataset, description],
  );

  if (!IS_AI_FEATURES_ENABLED) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-[800px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SparklesIcon className="h-5 w-5" />
            AI Dataset Assistant
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="description" className="text-sm font-medium">
              Description
            </Label>
            <motion.div
              className="relative w-full"
              animate={isShaking ? { x: [0, -8, 8, -8, 8, 0] } : { x: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              onAnimationComplete={() => setIsShaking(false)}
            >
              <Textarea
                id="description"
                disabled={isLoading}
                onKeyDown={handleKeyDown}
                placeholder="Describe the dataset you want to generate. E.g., 'Create sample data for a customer feedback system with names, emails, and satisfaction ratings'"
                className="mt-1 min-h-[80px]"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </motion.div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Schema Generation</Label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="space-y-3">
                <div
                  className={`flex items-start space-x-2 rounded-md p-3 transition-colors ${
                    !rowSchema ? "border border-blue-200 bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="mt-0.5">
                    <SparklesIcon
                      className={`h-4 w-4 ${!rowSchema ? "text-blue-600" : "text-gray-600"}`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-sm font-medium ${
                          !rowSchema ? "text-blue-900" : "text-gray-900"
                        }`}
                      >
                        Automatic Schema (Recommended)
                      </p>
                      {!rowSchema && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          Active
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${!rowSchema ? "text-blue-700" : "text-gray-600"}`}>
                      AI analyzes your description and prompt blueprint to automatically generate
                      the perfect schema for your dataset
                    </p>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-gray-50 px-2 text-gray-500">OR</span>
                  </div>
                </div>

                <div
                  className={`flex items-start space-x-2 rounded-md p-3 transition-colors ${
                    rowSchema ? "border border-blue-200 bg-blue-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="mt-0.5">
                    <svg
                      className={`h-4 w-4 ${rowSchema ? "text-blue-600" : "text-gray-600"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <p
                            className={`text-sm font-medium ${
                              rowSchema ? "text-blue-900" : "text-gray-900"
                            }`}
                          >
                            Manual Schema Definition
                          </p>
                          {rowSchema && (
                            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                              Active
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${rowSchema ? "text-blue-700" : "text-gray-600"}`}>
                          Define your own custom schema structure
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSchemaEditor(!showSchemaEditor)}
                        disabled={isLoading}
                        className={`relative whitespace-nowrap ${
                          rowSchema &&
                          !showSchemaEditor &&
                          "after:absolute after:right-1 after:top-1 after:h-2 after:w-2 after:rounded-full after:bg-blue-500"
                        }`}
                      >
                        {getSchemaButtonText(showSchemaEditor, rowSchema)}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {showSchemaEditor && (
              <div className="mt-3">
                <div className="overflow-hidden rounded-lg border border-gray-200 p-4">
                  <SchemaEditor
                    key={showSchemaEditor ? "opened" : "closed"}
                    schema={rowSchema || INTERACTIVE_EDITOR_DEFAULT_SCHEMA}
                    onSubmit={newSchema => {
                      setRowSchema(newSchema);
                      setShowSchemaEditor(false);
                    }}
                    readonly={isLoading}
                    miniMode
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setRowSchema(null);
                        setShowSchemaEditor(false);
                      }}
                      disabled={isLoading || !rowSchema}
                    >
                      Reset Schema
                    </Button>
                  </SchemaEditor>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <BasicTooltip shouldShow={!description} content="Enter a description to begin">
            <Button
              disabled={isLoading || !description.trim()}
              onClick={handleGenerateDataset}
              className="group relative"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="flex h-6 items-center overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={currentPhraseIndex}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="block"
                      >
                        {loadingPhrases[currentPhraseIndex]}
                      </motion.span>
                    </AnimatePresence>
                    <motion.div
                      layout
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="ml-2"
                    >
                      <LoadingSpinner size={4} />
                    </motion.div>
                  </div>
                </div>
              ) : (
                <span className="flex items-center gap-2">
                  Generate Dataset
                  <SparklesIcon2 className="h-4 w-4" />
                </span>
              )}
            </Button>
          </BasicTooltip>
        </div>
      </DialogContent>
    </Dialog>
  );
};
